import React, { useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '../Modal';
import {
  jobsError,
  useProofImage,
  useReviewApplication,
} from '../../hooks/useJobDigest';
import { Badge, date, fullName, INPUT, PRIMARY, SECONDARY } from './ui';

// Shows an application's screenshot and lets an admin approve or reject
// it. application needs id, status, note, review_note; student and job are
// optional context.
export default function ProofModal({ application, student, job, onClose }) {
  const { data: url, isLoading, error } = useProofImage(application?.id);
  const review = useReviewApplication();
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');

  const submit = (status) =>
    review.mutate(
      { id: application.id, status, note: status === 'rejected' ? note : '' },
      {
        onSuccess: () => {
          toast.success(status === 'approved' ? 'Approved' : 'Rejected');
          setRejecting(false);
          setNote('');
          onClose();
        },
        onError: (err) => toast.error(jobsError(err, "Couldn't save that.")),
      }
    );

  return (
    <Modal
      modalOpen={Boolean(application)}
      title="Application proof"
      wide
      onClose={() => !review.isLoading && onClose()}
    >
      {application && (
        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              {student && (
                <div className="font-medium text-gray-900">
                  {fullName(student)}{' '}
                  <span className="font-normal text-gray-500">
                    {student.email}
                  </span>
                </div>
              )}
              {job && (
                <div className="text-gray-700">
                  {job.url ? (
                    <a
                      href={job.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-claret-500 hover:underline"
                    >
                      {job.title}
                    </a>
                  ) : (
                    job.title
                  )}
                  {job.company && ` · ${job.company}`}
                </div>
              )}
              <div className="text-xs text-gray-500">
                Sent in {date(application.submitted_at, true)}
                {application.test && ' · from a test email'}
              </div>
            </div>
            <Badge status={application.status} />
          </div>

          {application.note && (
            <p className="rounded-lg bg-gray-50 px-3 py-2 text-gray-700">
              &ldquo;{application.note}&rdquo;
            </p>
          )}

          <div className="flex min-h-[12rem] items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
            {isLoading && <span className="text-gray-500">Loading image…</span>}
            {error && (
              <span className="text-red-700">
                {jobsError(error, "Couldn't load the image.")}
              </span>
            )}
            {url && (
              <a href={url} target="_blank" rel="noreferrer">
                <img
                  src={url}
                  alt="Screenshot of the application"
                  className="max-h-[60vh] w-auto object-contain"
                />
              </a>
            )}
          </div>

          {application.reviewed_by && (
            <p className="text-xs text-gray-500">
              {application.status === 'approved' ? 'Approved' : 'Reviewed'} by{' '}
              {application.reviewed_by} on {date(application.reviewed_at, true)}
              {application.review_note && `: ${application.review_note}`}
            </p>
          )}

          {rejecting ? (
            <div className="space-y-2">
              <label className="block space-y-1">
                <span className="text-xs font-medium text-gray-700">
                  Why is it rejected? The graduate will see this.
                </span>
                <textarea
                  rows={2}
                  maxLength={500}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="e.g. The screenshot doesn't show an application for this job."
                  className={`${INPUT} w-full`}
                />
              </label>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className={SECONDARY}
                  onClick={() => setRejecting(false)}
                >
                  Back
                </button>
                <button
                  type="button"
                  className={PRIMARY}
                  disabled={!note.trim() || review.isLoading}
                  onClick={() => submit('rejected')}
                >
                  Reject
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className={SECONDARY}
                disabled={review.isLoading || application.status === 'rejected'}
                onClick={() => setRejecting(true)}
              >
                Reject
              </button>
              <button
                type="button"
                className={PRIMARY}
                disabled={review.isLoading || application.status === 'approved'}
                onClick={() => submit('approved')}
              >
                Approve
              </button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
